import type { Statistics } from 'BrokerTsLib/src/dtos'
import { getHistoricalLabel, getOffsetHistoricalPeriod, getTimeRange, isDynamicPeriod, useTimeRange } from 'BrokerTsLib/src/hooks'
import { useEffect, useMemo } from 'react'

import { useApp } from '@/hooks/useApp'
import { useGetStatistics } from '@/hooks/useStatistics'

export interface UseStatsDataReturn {
  stats: Statistics
  adjustedPreviousStats: Statistics
  isLoadingStats: boolean
  anyData: boolean
  timeRange: ReturnType<typeof useTimeRange>
  previousTimeRangeLabel: string
  adjustedPreviousTimeRangeLabel: string
  reload: (force?: boolean) => void,
}

export const useStatsData = (ouId?: number, userId?: string, includeCategories?: boolean): UseStatsDataReturn => {
  const { product } = useApp()
  const { data: dataStats, mutateAsync: loadStats, isLoading } = useGetStatistics()
  const { data: dateadjustedPreviousStats, mutateAsync: loadPreviousStats } = useGetStatistics()

  const timeRange = useTimeRange()
  const previousHistoricalPeriod = useMemo(() =>
    getOffsetHistoricalPeriod(timeRange.historicalPeriod, -1)
  , [timeRange.historicalPeriod])
  const previousTimeRange = useMemo(() =>
    getTimeRange(previousHistoricalPeriod?.[0] ?? null, previousHistoricalPeriod?.[1] ?? null, null)
  , [previousHistoricalPeriod])
  const previousTimeRangeLabel = getHistoricalLabel(previousHistoricalPeriod?.[0] ?? null, previousHistoricalPeriod?.[1] ?? null)

  const currentStatsParams = useMemo(() => ({
    from: timeRange.timeRange.dateFrom,
    to: timeRange.timeRange.dateTo,
    force: false,
    product: product ?? undefined,
    ouId,
    userId,
    includeCategories
  }), [timeRange.timeRange.dateFrom, timeRange.timeRange.dateTo, product, ouId, userId])

  const previousStatsParams = useMemo(() => ({
    from: previousTimeRange.dateFrom,
    to: previousTimeRange.dateTo,
    force: false,
    product: product ?? undefined,
    ouId,
    userId,
    includeCategories
  }), [previousTimeRange.dateFrom, previousTimeRange.dateTo, product, ouId, userId])

  useEffect(() => {
    loadStats(currentStatsParams)
  }, [currentStatsParams, loadStats])

  useEffect(() => {
    loadPreviousStats(previousStatsParams)
  }, [previousStatsParams, loadPreviousStats])

  const stats = dataStats ?? {} as Statistics
  const previousStats = useMemo(() => dateadjustedPreviousStats ?? {} as Statistics, [dateadjustedPreviousStats])
  const isLoadingStats = !dataStats || isLoading
  const anyData = isLoadingStats || (stats.pocetSmluv + stats.pocetVytvorenych == 0)

  // Adjust previous stats, so that when current period is in progress, the previous stats are also adjusted to the same portion of the previous period.
  const adjustedPreviousStats: Statistics = useMemo(() => 
    previousStats && isDynamicPeriod(timeRange.historicalPeriod) ? {
      predepanePojistne: previousStats.predepanePojistne * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      pocetSmluv: previousStats.pocetSmluv * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      pocetVytvorenych: previousStats.pocetVytvorenych * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      pocetKlientu: previousStats.pocetKlientu * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      pocetKlientuVozidel: previousStats.pocetKlientuVozidel * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      pocetDoplnku: previousStats.pocetDoplnku * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
      insurers: previousStats.insurers?.map(insurer => ({
        ...insurer,
        price: insurer.price * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime()),
        count: insurer.count * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime())
      })),
      categories: previousStats.categories?.map(category => ({
        ...category,
        total: category.total * (timeRange.timeRange.dateTo.getTime() - timeRange.timeRange.dateFrom.getTime()) / (previousTimeRange.dateTo.getTime() - previousTimeRange.dateFrom.getTime())
      }))
    } : previousStats,
  [previousStats, timeRange.historicalPeriod, timeRange.timeRange.dateFrom, timeRange.timeRange.dateTo, previousTimeRange.dateFrom, previousTimeRange.dateTo])

  const adjustedPreviousTimeRangeLabel = isDynamicPeriod(timeRange.historicalPeriod) ? previousTimeRangeLabel + ' (za stejný časový úsek)' : previousTimeRangeLabel

  const reload = (force?: boolean) => {
    loadStats({
      from: timeRange.timeRange.dateFrom,
      to: timeRange.timeRange.dateTo,
      force: force ?? false,
      product: product ?? undefined,
      ouId,
      userId
    })

    loadPreviousStats({
      from: previousTimeRange.dateFrom,
      to: previousTimeRange.dateTo,
      force: false,
      product: product ?? undefined,
      ouId,
      userId
    })
  }

  return {
    stats,
    adjustedPreviousStats,
    isLoadingStats,
    anyData,
    timeRange,
    previousTimeRangeLabel,
    adjustedPreviousTimeRangeLabel,
    reload
  }
}
